<template>
  <span v-text="fullString" />
</template>

<script setup>
import { useI18n } from 'vue-i18n';

const { locale } = useI18n();
const $bct = inject('translator');

const haitianCreoleFormatting = {
  long: [
    'Janvye',
    'Fevriye',
    'Mas',
    'Avil',
    'Me',
    'Jen',
    'Jiyè',
    'Out',
    'Septanm',
    'Oktòb',
    'Novamn',
    'Desamn',
  ],
};

const props = defineProps({
  date: {
    type: String,
    default: null,
  },
  hideYear: Boolean,
  nulltext: {
    type: String,
    default: '',
  },
  prefix: {
    type: String,
    default: '',
  },
  prefixSeparator: {
    type: String,
    default: '',
  },
  suffix: {
    type: String,
    default: '',
  },
  shortMonth: {
    boolean: false,
    type: Boolean,
  },
  timezone: {
    default() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    },
    type: String,
  },
});

const fullString = computed(() => {
  const str = [];
  if (props.prefix) {
    str.push(`${$bct(props.prefix)}${props.prefixSeparator}`);
  }

  str.push(localeDateTimeString.value);

  if (props.suffix) {
    str.push($bct(props.suffix));
  }

  return str.join(' ');
});

const localeDateTimeString = computed(() => {
  if (!props.date) {
    return props.nulltext;
  }
  const date = new Date(props.date);
  const resolvedTimeZone = resolveTimeZone(props.timezone);
  const wallClockTimeZone = locale.value === 'ht' ? 'America/Port-au-Prince' : resolvedTimeZone;
  const shouldShowTime = dateTimeShouldIncludeTime(props.date, date, wallClockTimeZone);

  if (locale.value === 'ht') {
    const day = date.getUTCDate();
    const month = haitianCreoleFormatting.long[date.getUTCMonth()];
    const year = date.getUTCFullYear();

    if (shouldShowTime) {
      const haitianTimeZone = date.toLocaleTimeString('ht', {
        timeZone: 'America/Port-au-Prince',
      });
      return `${day} ${month} ${year}, ${haitianTimeZone}`;
    } else {
      return `${day} ${month} ${year}`;
    }
  } else {
    const options = {
      month: props.shortMonth ? 'short' : 'long',
      day: 'numeric',
      timeZone: resolvedTimeZone,
    };

    if (shouldShowTime) {
      options.hour = 'numeric';
      options.minute = 'numeric';
    }

    if (!props.hideYear) {
      options.year = 'numeric';
    }
    return shouldShowTime
      ? date.toLocaleString(locale.value, options)
      : date.toLocaleDateString(locale.value, options);
  }
});

function dateTimeShouldIncludeTime(rawDateInput, parsedDate, timeZone) {
  if (typeof rawDateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rawDateInput.trim())) {
    return false;
  }
  if (wallClockTotalSeconds(parsedDate, timeZone) !== 0) {
    return true;
  }
  if (typeof rawDateInput === 'string' && hasNonZeroFractionalSecondsInIso(rawDateInput)) {
    return true;
  }
  return false;
}

function wallClockTotalSeconds(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    hour12: false,
    minute: 'numeric',
    second: 'numeric',
  }).formatToParts(date);
  let hour = Number(parts.find((part) => part.type === 'hour')?.value ?? 0);
  if (hour === 24) {
    hour = 0;
  }
  const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? 0);
  const second = Number(parts.find((part) => part.type === 'second')?.value ?? 0);
  return hour * 3600 + minute * 60 + second;
}

function hasNonZeroFractionalSecondsInIso(rawDateInput) {
  const match = rawDateInput.match(/T\d{2}:\d{2}:\d{2}\.(\d+)/);
  if (!match) {
    return false;
  }
  return Number(match[1]) !== 0;
}

function resolveTimeZone(timezone) {
  if (timezone.includes('Etc/Unknown')) {
    return 'UTC';
  }
  return timezone;
}
</script>
